import { writable, derived, get } from 'svelte/store';
import type { Flashcard, FlashcardTargetLanguage, FlashcardCategory } from '../types';
import { supabase } from '../lib/supabase';
import { offlineStorage } from '../lib/offline-storage';
import { syncService, isOnline } from '../lib/sync-service';
import { XPService } from '../lib/xp-service';

// Reactive state using Svelte stores
export const flashcards = writable<Flashcard[]>([]);
export const targetLanguages = writable<FlashcardTargetLanguage[]>([]);
export const categories = writable<FlashcardCategory[]>([]);
export const selectedTargetLanguage = writable<FlashcardTargetLanguage | null>(null);
export const selectedCategory = writable<FlashcardCategory | null>(null);
export const isLoading = writable<boolean>(false);
export const appError = writable<string | null>(null);

// Derived state using Svelte derived stores
export const totalCards = derived(flashcards, ($flashcards) => $flashcards.length);
export const learnedCards = derived(flashcards, ($flashcards) =>
  $flashcards.filter((card) => card.isLearned)
);
export const unlearnedCards = derived(flashcards, ($flashcards) =>
  $flashcards.filter((card) => !card.isLearned)
);
export const learnedCount = derived(learnedCards, ($learnedCards) => $learnedCards.length);
export const unlearnedCount = derived(unlearnedCards, ($unlearnedCards) => $unlearnedCards.length);

// Filtered flashcards by selected language and category
export const filteredFlashcards = derived(
  [flashcards, selectedTargetLanguage, selectedCategory],
  ([$flashcards, $selectedLanguage, $selectedCategory]) => {
    let result = $flashcards;

    if ($selectedLanguage) {
      result = result.filter((card) => card.targetLanguage.id === $selectedLanguage.id);
    }

    if ($selectedCategory) {
      result = result.filter((card) => card.category?.id === $selectedCategory.id);
    }

    return result;
  }
);

// Filtered categories by selected language
export const filteredCategories = derived(
  [categories, selectedTargetLanguage],
  ([$categories, $selectedLanguage]) => {
    if (!$selectedLanguage) return $categories;
    return $categories.filter((cat) => cat.targetLanguage.id === $selectedLanguage.id);
  }
);

const DEFAULT_CATEGORY_NAME = 'Obecná kategorie';
const normalizeCategoryName = (name: string) => name.trim().toLowerCase();
const normalizeLanguageCode = (code: string) => code?.toLowerCase().split('-')[0] ?? '';

// Valid ISO 639-1 language codes
const VALID_ISO_639_1_CODES = new Set([
  'aa',
  'ab',
  'ae',
  'af',
  'ak',
  'am',
  'an',
  'ar',
  'as',
  'av',
  'ay',
  'az',
  'ba',
  'be',
  'bg',
  'bh',
  'bi',
  'bm',
  'bn',
  'bo',
  'br',
  'bs',
  'ca',
  'ce',
  'ch',
  'co',
  'cr',
  'cs',
  'cu',
  'cv',
  'cy',
  'da',
  'de',
  'dv',
  'dz',
  'ee',
  'el',
  'en',
  'eo',
  'es',
  'et',
  'eu',
  'fa',
  'ff',
  'fi',
  'fj',
  'fo',
  'fr',
  'fy',
  'ga',
  'gd',
  'gl',
  'gn',
  'gv',
  'ha',
  'he',
  'hi',
  'ho',
  'hr',
  'ht',
  'hu',
  'hy',
  'hz',
  'ia',
  'id',
  'ie',
  'ig',
  'ii',
  'ik',
  'in',
  'io',
  'is',
  'it',
  'iu',
  'iw',
  'ja',
  'jv',
  'ka',
  'kg',
  'ki',
  'kj',
  'kk',
  'kl',
  'km',
  'kn',
  'ko',
  'kr',
  'ks',
  'ku',
  'kv',
  'kw',
  'ky',
  'la',
  'lb',
  'lg',
  'li',
  'ln',
  'lo',
  'lt',
  'lu',
  'lv',
  'mg',
  'mh',
  'mi',
  'mk',
  'ml',
  'mn',
  'mr',
  'ms',
  'mt',
  'my',
  'na',
  'nb',
  'nd',
  'ne',
  'ng',
  'nl',
  'nn',
  'no',
  'nr',
  'nv',
  'oc',
  'oj',
  'om',
  'or',
  'os',
  'pa',
  'pi',
  'pl',
  'ps',
  'pt',
  'qu',
  'rm',
  'rn',
  'ro',
  'ru',
  'rw',
  'sa',
  'sc',
  'sd',
  'se',
  'sg',
  'sh',
  'si',
  'sk',
  'sl',
  'sm',
  'sn',
  'so',
  'sq',
  'sr',
  'ss',
  'st',
  'su',
  'sv',
  'sw',
  'ta',
  'te',
  'tg',
  'th',
  'ti',
  'tk',
  'tl',
  'tn',
  'to',
  'tr',
  'ts',
  'tt',
  'tw',
  'ty',
  'ug',
  'uk',
  'ur',
  'uz',
  've',
  'vi',
  'vo',
  'wa',
  'wo',
  'xh',
  'yi',
  'yo',
  'za',
  'zh',
  'zu',
]);

// Helper function to parse Date objects from cached data
function parseDatesInData<T extends { createdAt?: any; updatedAt?: any }>(data: T[]): T[] {
  return data.map((item) => ({
    ...item,
    createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
    updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
  }));
}

async function getAuthenticatedUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

function resolveTargetLanguage(language?: FlashcardTargetLanguage | null): FlashcardTargetLanguage {
  const explicit = language ?? get(selectedTargetLanguage);
  if (explicit) {
    return explicit;
  }

  const fallback = get(targetLanguages)[0];
  if (!fallback) {
    throw new Error('Není vybrán žádný cílový jazyk.');
  }
  return fallback;
}

function mapCategoryRowToModel(
  row: {
    id: string;
    name: string;
    description: string | null;
    target_language_id: string;
    created_at?: string | null;
    updated_at?: string | null;
  },
  language?: FlashcardTargetLanguage
): FlashcardCategory | null {
  const targetLanguage =
    language ?? get(targetLanguages).find((lang) => lang.id === row.target_language_id);

  if (!targetLanguage) {
    console.warn('Kategorie bez přiřazeného jazyka:', row);
    return null;
  }

  return {
    id: row.id,
    name: row.name,
    description: row.description || undefined,
    targetLanguage,
    flashcards: [],
    createdAt: row.created_at ? new Date(row.created_at) : new Date(),
    updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
  };
}

async function persistCategoriesToCache() {
  await offlineStorage.saveCategories(get(categories));
}

async function persistFlashcardsToCache() {
  await offlineStorage.saveFlashcards(get(flashcards));
}

async function addCategory(
  name: string,
  description?: string,
  language?: FlashcardTargetLanguage
): Promise<FlashcardCategory> {
  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error('Název kategorie je povinný.');
  }

  const targetLanguage = resolveTargetLanguage(language);
  const now = new Date();
  const tempId = `temp_${Date.now()}_${Math.random()}`;

  const optimisticCategory: FlashcardCategory = {
    id: tempId,
    name: trimmedName,
    description: description?.trim() || undefined,
    targetLanguage,
    flashcards: [],
    createdAt: now,
    updatedAt: now,
  };

  categories.update((cats) => [...cats, optimisticCategory]);
  await persistCategoriesToCache();

  if (get(isOnline)) {
    try {
      const userId = await getAuthenticatedUserId();
      if (!userId) {
        throw new Error('Uživatel není přihlášen.');
      }

      const { data, error } = await supabase
        .from('flashcard_categories')
        .insert({
          user_id: userId,
          target_language_id: targetLanguage.id,
          name: trimmedName,
          description: description?.trim() || null,
        })
        .select('*')
        .single();

      if (error) throw error;

      const persisted = mapCategoryRowToModel(data, targetLanguage);
      if (!persisted) {
        throw new Error('Nepodařilo se vytvořit kategorii.');
      }

      categories.update((cats) => cats.map((cat) => (cat.id === tempId ? persisted : cat)));
      await persistCategoriesToCache();
      return persisted;
    } catch (error) {
      console.error('Nepodařilo se synchronizovat kategorii:', error);
      await offlineStorage.addToSyncQueue('create_category', optimisticCategory);
      appError.set(error instanceof Error ? error.message : 'Nepodařilo se uložit kategorii');
      throw error instanceof Error ? error : new Error('Nepodařilo se uložit kategorii');
    }
  } else {
    await offlineStorage.addToSyncQueue('create_category', optimisticCategory);
  }

  return optimisticCategory;
}

async function updateCategory(
  id: string,
  updates: { name?: string; description?: string }
): Promise<FlashcardCategory> {
  const existing = get(categories).find((cat) => cat.id === id);
  if (!existing) {
    throw new Error('Kategorie nebyla nalezena.');
  }

  const updatedCategory: FlashcardCategory = {
    ...existing,
    name: updates.name?.trim() || existing.name,
    description: updates.description?.trim() || undefined,
    updatedAt: new Date(),
  };

  categories.update((cats) => cats.map((cat) => (cat.id === id ? updatedCategory : cat)));
  flashcards.update((cards) =>
    cards.map((card) => (card.category?.id === id ? { ...card, category: updatedCategory } : card))
  );

  await persistCategoriesToCache();
  await persistFlashcardsToCache();

  if (get(isOnline)) {
    try {
      const { error } = await supabase
        .from('flashcard_categories')
        .update({
          name: updatedCategory.name,
          description: updatedCategory.description || null,
        })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Nepodařilo se aktualizovat kategorii:', error);
      await offlineStorage.addToSyncQueue('update_category', updatedCategory);
      appError.set(error instanceof Error ? error.message : 'Nepodařilo se aktualizovat kategorii');
      throw error instanceof Error ? error : new Error('Nepodařilo se aktualizovat kategorii');
    }
  } else {
    await offlineStorage.addToSyncQueue('update_category', updatedCategory);
  }

  return updatedCategory;
}

async function deleteCategory(id: string): Promise<void> {
  const existing = get(categories).find((cat) => cat.id === id);
  if (!existing) {
    return;
  }

  categories.update((cats) => cats.filter((cat) => cat.id !== id));
  flashcards.update((cards) =>
    cards.map((card) => (card.category?.id === id ? { ...card, category: undefined } : card))
  );

  await persistCategoriesToCache();
  await persistFlashcardsToCache();

  if (get(isOnline)) {
    try {
      const { error } = await supabase.from('flashcard_categories').delete().eq('id', id);
      if (error) throw error;
    } catch (error) {
      console.error('Nepodařilo se smazat kategorii:', error);
      await offlineStorage.addToSyncQueue('delete_category', id);
      appError.set(error instanceof Error ? error.message : 'Nepodařilo se smazat kategorii');
      throw error instanceof Error ? error : new Error('Nepodařilo se smazat kategorii');
    }
  } else {
    await offlineStorage.addToSyncQueue('delete_category', id);
  }
}

async function ensureDefaultCategory(
  language?: FlashcardTargetLanguage
): Promise<FlashcardCategory | null> {
  try {
    const targetLanguage = resolveTargetLanguage(language);
    const existingForLanguage = get(categories).filter(
      (cat) => cat.targetLanguage.id === targetLanguage.id
    );

    const existingDefault = existingForLanguage.find(
      (cat) => normalizeCategoryName(cat.name) === normalizeCategoryName(DEFAULT_CATEGORY_NAME)
    );

    if (existingDefault) {
      return existingDefault;
    }

    return await addCategory(DEFAULT_CATEGORY_NAME, undefined, targetLanguage);
  } catch (error) {
    console.error('Nepodařilo se zajistit výchozí kategorii:', error);
    return null;
  }
}

export const categoryActions = {
  addCategory,
  updateCategory,
  deleteCategory,
  ensureDefaultCategory,
};

async function ensureDefaultCategoriesForLanguages(languages: FlashcardTargetLanguage[]) {
  if (!languages.length) return;
  await Promise.all(languages.map((language) => categoryActions.ensureDefaultCategory(language)));
}

// Actions
export const flashcardActions = {
  // Load all data - offline-first approach
  loadData: async () => {
    console.log('🔄 Starting loadData (offline-first)...');
    isLoading.set(true);
    appError.set(null);

    try {
      // Step 1: Load from local cache immediately for instant UI
      console.log('📦 Loading from local cache...');
      const [cachedFlashcards, cachedLanguages, cachedCategories] = await Promise.all([
        offlineStorage.getFlashcards(),
        offlineStorage.getLanguages(),
        offlineStorage.getCategories(),
      ]);

      // If we have cached data, display it immediately
      if (cachedLanguages?.data && cachedLanguages.data.length > 0) {
        console.log('✅ Using cached data');
        targetLanguages.set(cachedLanguages.data);
        categories.set(parseDatesInData(cachedCategories?.data || []));
        flashcards.set(parseDatesInData(cachedFlashcards?.data || []));

        // Set default language
        if (cachedLanguages.data.length > 0) {
          selectedTargetLanguage.set(cachedLanguages.data[0]);
        }
      }

      // Step 2: Sync in background (if online)
      if (get(isOnline)) {
        console.log('🌐 Syncing with server in background...');
        await syncService.sync();

        // Reload from cache after sync
        const [syncedFlashcards, syncedLanguages, syncedCategories] = await Promise.all([
          offlineStorage.getFlashcards(),
          offlineStorage.getLanguages(),
          offlineStorage.getCategories(),
        ]);

        if (syncedLanguages?.data) {
          targetLanguages.set(syncedLanguages.data);
          categories.set(parseDatesInData(syncedCategories?.data || []));
          flashcards.set(parseDatesInData(syncedFlashcards?.data || []));

          // Preserve or set default language
          const currentLanguage = get(selectedTargetLanguage);
          if (!currentLanguage && syncedLanguages.data.length > 0) {
            selectedTargetLanguage.set(syncedLanguages.data[0]);
          }
        }

        console.log('✅ Sync completed');
      } else {
        console.log('📴 Offline mode - using cached data only');
      }

      // If no cached data and offline, try loading from server anyway
      if (!cachedLanguages?.data && !get(isOnline)) {
        console.log('⚠️ No cached data and offline - attempting server load...');
        await flashcardActions.loadDataFromServer();
      }
    } catch (err) {
      console.error('❌ Error in loadData:', err);
      appError.set(err instanceof Error ? err.message : 'Unknown error occurred');

      // Fallback to server load if cache fails
      try {
        await flashcardActions.loadDataFromServer();
      } catch (serverErr) {
        console.error('❌ Server fallback also failed:', serverErr);
      }
    } finally {
      try {
        await ensureDefaultCategoriesForLanguages(get(targetLanguages));
      } catch (defaultError) {
        console.error('Nepodařilo se vytvořit výchozí kategorii:', defaultError);
      }
      isLoading.set(false);
    }
  },

  // Legacy server-only load (fallback)
  loadDataFromServer: async () => {
    console.log('🔄 Starting loadDataFromServer (legacy)...');
    isLoading.set(true);
    appError.set(null);

    try {
      // Check authentication status
      console.log('🔐 Checking authentication...');
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      console.log('🔐 Auth status:', { user: !!user, error: authError?.message });

      // If not authenticated, try anonymous access or show warning
      if (!user && !import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY) {
        console.warn(
          '⚠️ User not authenticated and no service role key found. Data may not load due to RLS policies.'
        );
        console.warn('💡 For development, add VITE_SUPABASE_SERVICE_ROLE_KEY to .env file');
        console.warn(
          '🔗 Get service role key from: https://supabase.com/dashboard/project/opsmotbppewoufmuxwlx/settings/api'
        );
      }

      // Test connection first
      console.log('🔗 Testing Supabase connection...');
      const { data: testData, error: testError } = await supabase
        .from('flashcards')
        .select('count', { count: 'exact', head: true });

      console.log('🔗 Connection test result:', { testData, testError });

      // If connection test fails, try to sign in anonymously or handle auth
      if (testError) {
        console.warn('⚠️ Connection test failed, trying anonymous sign in...');
        // For development, try anonymous access
        // In production, you would redirect to login
      }

      // Load target languages (try simple query first)
      console.log('🌍 Loading target languages (simple)...');
      const { data: simpleLanguages, error: simpleLangError } = await supabase
        .from('flashcard_target_languages')
        .select('id, name, code, is_custom, is_rtl')
        .order('name');

      console.log('🌍 Simple languages result:', { simpleLanguages, simpleLangError });

      // If simple query fails, try without auth
      let languages = simpleLanguages;
      let langError = simpleLangError;

      if (simpleLangError) {
        console.log('🌍 Retrying languages without joins...');
        const { data: fallbackLanguages, error: fallbackLangError } = await supabase
          .from('flashcard_target_languages')
          .select('*');

        languages = fallbackLanguages;
        langError = fallbackLangError;
        console.log('🌍 Fallback languages result:', { languages, langError });
      }

      if (langError) {
        console.error('❌ Language loading error:', langError);
        throw langError;
      }

      const userId = user?.id ?? null;

      let cats: any[] | null = null;
      let catError: any = null;

      if (userId) {
        console.log('📂 Loading categories (simple)...');
        const { data: simpleCats, error: simpleCatError } = await supabase
          .from('flashcard_categories')
          .select('id, name, description, target_language_id, created_at, updated_at')
          .eq('user_id', userId)
          .order('name');

        console.log('📂 Simple categories result:', { simpleCats, simpleCatError });

        cats = simpleCats;
        catError = simpleCatError;

        if (simpleCatError) {
          console.log('📂 Retrying categories without joins...');
          const { data: fallbackCats, error: fallbackCatError } = await supabase
            .from('flashcard_categories')
            .select('*')
            .eq('user_id', userId);

          cats = fallbackCats;
          catError = fallbackCatError;
          console.log('📂 Fallback categories result:', { cats, catError });
        }

        if (catError) {
          console.error('❌ Categories loading error:', catError);
          throw catError;
        }
      } else {
        cats = [];
      }

      // Load flashcards (only for authenticated users)
      let cards: any[] | null = null;
      let cardError: any = null;

      if (userId) {
        console.log('📚 Loading flashcards (simple)...');
        const { data: simpleCards, error: simpleCardError } = await supabase
          .from('flashcards')
          .select(
            'id, primary_text, target_text, phonetic, audio_url, is_learned, target_language_id, category_id, examples, created_at, updated_at'
          )
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        console.log('📚 Simple flashcards result:', { simpleCards, simpleCardError });

        cards = simpleCards;
        cardError = simpleCardError;

        if (simpleCardError) {
          console.log('📚 Retrying flashcards without joins...');
          const { data: fallbackCards, error: fallbackCardError } = await supabase
            .from('flashcards')
            .select('*')
            .eq('user_id', userId);

          cards = fallbackCards;
          cardError = fallbackCardError;
          console.log('📚 Fallback flashcards result:', { cards, cardError });
        }

        if (cardError) {
          console.error('❌ Flashcards loading error:', cardError);
          throw cardError;
        }
      } else {
        cards = [];
      }

      // Transform data to match our types
      console.log('🔄 Transforming data...');
      console.log('📊 Raw data counts:', {
        languages: languages?.length || 0,
        categories: cats?.length || 0,
        flashcards: cards?.length || 0,
      });

      const transformedLanguages: FlashcardTargetLanguage[] =
        languages?.map((lang) => ({
          id: lang.id,
          name: lang.name,
          code: lang.code,
          is_custom: lang.is_custom,
          is_rtl: lang.is_rtl ?? false,
        })) || [];

      // Create language lookup map for categories
      const languageMap = new Map(transformedLanguages.map((lang) => [lang.id, lang]));

      const transformedCategories: FlashcardCategory[] =
        (cats
          ?.map((cat) => {
            const targetLanguage = languageMap.get(cat.target_language_id);
            if (!targetLanguage) {
              console.warn('⚠️ Category without matching language:', cat);
              return null;
            }
            return {
              id: cat.id,
              name: cat.name,
              description: cat.description || undefined,
              targetLanguage,
              flashcards: [],
              createdAt: new Date(cat.created_at),
              updatedAt: new Date(cat.updated_at),
            };
          })
          .filter(Boolean) as FlashcardCategory[]) || [];

      // Create category lookup map for flashcards
      const categoryMap = new Map(transformedCategories.map((cat) => [cat.id, cat]));

      const transformedCards: Flashcard[] =
        (cards
          ?.map((card) => {
            const targetLanguage = languageMap.get(card.target_language_id);
            const category = card.category_id ? categoryMap.get(card.category_id) : undefined;

            if (!targetLanguage) {
              console.warn('⚠️ Flashcard without matching language:', card);
              return null;
            }

            return {
              id: card.id,
              primary: card.primary_text,
              target: card.target_text,
              phonetic: card.phonetic || '',
              audioUrl: card.audio_url || undefined,
              isLearned: card.is_learned,
              targetLanguage,
              category,
              examples: Array.isArray(card.examples) ? (card.examples as string[]) : [],
              createdAt: new Date(card.created_at),
              updatedAt: new Date(card.updated_at),
            };
          })
          .filter(Boolean) as Flashcard[]) || [];

      console.log('✅ Data transformation complete:', {
        languages: transformedLanguages.length,
        categories: transformedCategories.length,
        flashcards: transformedCards.length,
      });

      targetLanguages.set(transformedLanguages);
      categories.set(transformedCategories);
      flashcards.set(transformedCards);

      // Set default selected language (first one)
      if (transformedLanguages.length > 0) {
        console.log('Setting default language:', transformedLanguages[0]);
        selectedTargetLanguage.set(transformedLanguages[0]);
      } else {
        console.log('No languages found');
        selectedTargetLanguage.set(null);
      }
    } catch (err) {
      appError.set(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      try {
        await ensureDefaultCategoriesForLanguages(get(targetLanguages));
      } catch (defaultError) {
        console.error('Nepodařilo se vytvořit výchozí kategorii:', defaultError);
      }
      isLoading.set(false);
    }
  },

  selectTargetLanguage: (language: FlashcardTargetLanguage) => {
    selectedTargetLanguage.set(language);
  },

  selectCategory: (category: FlashcardCategory | null) => {
    selectedCategory.set(category);
  },

  addFlashcard: async (
    newCard: Omit<Flashcard, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<Flashcard> => {
    try {
      const targetLanguage = resolveTargetLanguage(newCard.targetLanguage);

      // Create temporary ID for optimistic update
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const now = new Date();

      // Create flashcard object with temp ID
      const optimisticCard: Flashcard = {
        id: tempId,
        ...newCard,
        targetLanguage,
        createdAt: now,
        updatedAt: now,
      };

      // Optimistic update - add to store immediately
      flashcards.update((cards) => [...cards, optimisticCard]);

      await persistFlashcardsToCache();

      // If online, try to sync immediately
      if (get(isOnline)) {
        try {
          const userId = await getAuthenticatedUserId();
          if (!userId) {
            throw new Error('Uživatel není přihlášen.');
          }

          const { data, error: insertError } = await supabase
            .from('flashcards')
            .insert({
              user_id: userId,
              primary_text: newCard.primary,
              target_text: newCard.target,
              phonetic: newCard.phonetic,
              audio_url: newCard.audioUrl,
              is_learned: newCard.isLearned,
              target_language_id: targetLanguage.id,
              category_id: newCard.category?.id || null,
              examples: newCard.examples || [],
            })
            .select(
              `
              *,
              targetLanguage:flashcard_target_languages(*),
              category:flashcard_categories(*)
            `
            )
            .single();

          if (insertError) throw insertError;

          // Replace temp card with real card from server
          const transformedCard: Flashcard = {
            id: data.id,
            primary: data.primary_text,
            target: data.target_text,
            phonetic: data.phonetic || '',
            audioUrl: data.audio_url || undefined,
            isLearned: data.is_learned,
            targetLanguage: {
              id: data.targetLanguage.id,
              name: data.targetLanguage.name,
              code: data.targetLanguage.code,
            },
            category: data.category
              ? {
                  id: data.category.id,
                  name: data.category.name,
                  description: data.category.description || undefined,
                  targetLanguage: {
                    id: data.targetLanguage.id,
                    name: data.targetLanguage.name,
                    code: data.targetLanguage.code,
                  },
                  flashcards: [],
                  createdAt: new Date(data.category.created_at),
                  updatedAt: new Date(data.category.updated_at),
                }
              : undefined,
            examples: Array.isArray(data.examples) ? (data.examples as string[]) : [],
            createdAt: new Date(data.created_at),
            updatedAt: new Date(data.updated_at),
          };

          // Replace temp card with real one
          flashcards.update((cards) =>
            cards.map((card) => (card.id === tempId ? transformedCard : card))
          );

          await persistFlashcardsToCache();
          return transformedCard;
        } catch (syncError) {
          console.error('Failed to sync new flashcard:', syncError);
          // Keep optimistic update and add to sync queue
          await offlineStorage.addToSyncQueue('create_flashcard', optimisticCard);
        }
      } else {
        // Offline - add to sync queue
        await offlineStorage.addToSyncQueue('create_flashcard', optimisticCard);
      }

      return optimisticCard;
    } catch (err) {
      appError.set(err instanceof Error ? err.message : 'Failed to add flashcard');
      throw err instanceof Error ? err : new Error('Failed to add flashcard');
    }
  },

  updateFlashcard: async (id: string, updates: Partial<Flashcard>) => {
    try {
      // Optimistic update
      flashcards.update((cards) =>
        cards.map((card) =>
          card.id === id ? { ...card, ...updates, updatedAt: new Date() } : card
        )
      );

      // Update local cache
      const currentCards = get(flashcards);
      await offlineStorage.saveFlashcards(currentCards);

      // Get updated card for sync queue
      const updatedCard = currentCards.find((c) => c.id === id);

      // If online, try to sync immediately
      if (get(isOnline)) {
        try {
          const updateData: {
            primary_text?: string;
            target_text?: string;
            phonetic?: string;
            audio_url?: string;
            is_learned?: boolean;
            category_id?: string | null;
            examples?: string[];
          } = {};
          if (updates.primary !== undefined) updateData.primary_text = updates.primary;
          if (updates.target !== undefined) updateData.target_text = updates.target;
          if (updates.phonetic !== undefined) updateData.phonetic = updates.phonetic;
          if (updates.audioUrl !== undefined) updateData.audio_url = updates.audioUrl;
          if (updates.isLearned !== undefined) updateData.is_learned = updates.isLearned;
          if (updates.category !== undefined) updateData.category_id = updates.category?.id || null;
          if (updates.examples !== undefined) updateData.examples = updates.examples;

          const { data, error: updateError } = await supabase
            .from('flashcards')
            .update(updateData)
            .eq('id', id)
            .select(
              `
              *,
              targetLanguage:flashcard_target_languages(*),
              category:flashcard_categories(*)
            `
            )
            .maybeSingle();

          if (updateError) throw updateError;
          if (!data) {
            // No rows returned: often means the update matched 0 rows (RLS or missing id)
            console.warn('Update returned no rows for id:', id);
            // Keep optimistic update and add to sync queue
            if (updatedCard) {
              await offlineStorage.addToSyncQueue('update_flashcard', updatedCard);
            }
            return;
          }

          // Update with server response
          const transformedCard: Flashcard = {
            id: data.id,
            primary: data.primary_text,
            target: data.target_text,
            phonetic: data.phonetic || '',
            audioUrl: data.audio_url || undefined,
            isLearned: data.is_learned,
            targetLanguage: {
              id: data.targetLanguage.id,
              name: data.targetLanguage.name,
              code: data.targetLanguage.code,
            },
            category: data.category
              ? {
                  id: data.category.id,
                  name: data.category.name,
                  description: data.category.description || undefined,
                  targetLanguage: {
                    id: data.targetLanguage.id,
                    name: data.targetLanguage.name,
                    code: data.targetLanguage.code,
                  },
                  flashcards: [],
                  createdAt: new Date(data.category.created_at),
                  updatedAt: new Date(data.category.updated_at),
                }
              : undefined,
            examples: Array.isArray(data.examples) ? (data.examples as string[]) : [],
            createdAt: new Date(data.created_at),
            updatedAt: new Date(data.updated_at),
          };

          flashcards.update((cards) =>
            cards.map((card) => (card.id === id ? transformedCard : card))
          );

          // Update cache
          const syncedCards = get(flashcards);
          await offlineStorage.saveFlashcards(syncedCards);
        } catch (syncError) {
          console.error('Failed to sync update:', syncError);
          // Keep optimistic update and add to sync queue
          if (updatedCard) {
            await offlineStorage.addToSyncQueue('update_flashcard', updatedCard);
          }
        }
      } else {
        // Offline - add to sync queue
        if (updatedCard) {
          await offlineStorage.addToSyncQueue('update_flashcard', updatedCard);
        }
      }
    } catch (err) {
      appError.set(err instanceof Error ? err.message : 'Failed to update flashcard');
    }
  },

  toggleLearned: async (id: string) => {
    try {
      // First get current value
      const currentCards = get(flashcards);
      const currentCard = currentCards.find((card) => card.id === id);
      if (!currentCard) return;

      const previousValue = currentCard.isLearned;
      const newValue = !previousValue;

      // Optimistic update: flip locally first
      flashcards.update((cards) =>
        cards.map((card) =>
          card.id === id ? { ...card, isLearned: newValue, updatedAt: new Date() } : card
        )
      );

      // Update local cache
      const updatedCards = get(flashcards);
      await offlineStorage.saveFlashcards(updatedCards);

      // Add XP if card is marked as learned
      if (newValue && get(isOnline)) {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (user) {
            await XPService.addXP(user.id, 'learned_word', { flashcard_id: id });
          }
        } catch (xpError) {
          console.error('Failed to add XP:', xpError);
          // Don't fail the whole operation if XP fails
        }
      }

      // If online, try to sync immediately
      if (get(isOnline)) {
        try {
          const { error: toggleError } = await supabase
            .from('flashcards')
            .update({ is_learned: newValue })
            .eq('id', id);

          if (toggleError) throw toggleError;
        } catch (syncError) {
          console.error('Failed to sync toggle:', syncError);
          // Keep optimistic update and add to sync queue
          const updatedCard = get(flashcards).find((c) => c.id === id);
          if (updatedCard) {
            await offlineStorage.addToSyncQueue('update_flashcard', updatedCard);
          }
        }
      } else {
        // Offline - add to sync queue
        const updatedCard = get(flashcards).find((c) => c.id === id);
        if (updatedCard) {
          await offlineStorage.addToSyncQueue('update_flashcard', updatedCard);
        }
      }
    } catch (err) {
      appError.set(err instanceof Error ? err.message : 'Failed to toggle learned status');
    }
  },

  deleteFlashcard: async (id: string) => {
    try {
      // Save card for potential sync queue
      const cardToDelete = get(flashcards).find((c) => c.id === id);

      // Optimistic delete
      flashcards.update((cards) => cards.filter((card) => card.id !== id));

      // Update local cache
      const updatedCards = get(flashcards);
      await offlineStorage.saveFlashcards(updatedCards);

      // If online, try to sync immediately
      if (get(isOnline)) {
        try {
          const { error: deleteError } = await supabase.from('flashcards').delete().eq('id', id);
          if (deleteError) throw deleteError;
        } catch (syncError) {
          console.error('Failed to sync delete:', syncError);
          // Add to sync queue for later
          await offlineStorage.addToSyncQueue('delete_flashcard', id);
        }
      } else {
        // Offline - add to sync queue
        await offlineStorage.addToSyncQueue('delete_flashcard', id);
      }
    } catch (err) {
      appError.set(err instanceof Error ? err.message : 'Failed to delete flashcard');
    }
  },

  // Add custom language
  addCustomLanguage: async (
    name: string,
    code: string,
    isRtl: boolean = false
  ): Promise<FlashcardTargetLanguage> => {
    try {
      const userId = await getAuthenticatedUserId();
      if (!userId) {
        throw new Error('Uživatel není přihlášen.');
      }

      // Validate code format (ISO 639-1)
      if (!code || code.length !== 2) {
        throw new Error('Kód musí být 2-znakový ISO 639-1 kód (např. "ja" pro japonštinu).');
      }

      const normalizedCode = code.toLowerCase();

      // Validate against known ISO 639-1 codes
      if (!VALID_ISO_639_1_CODES.has(normalizedCode)) {
        throw new Error(
          `Kód "${normalizedCode}" není platný ISO 639-1 kód. Zkontrolujte prosím správnost.`
        );
      }

      // Check if language already exists
      const existingLanguage = get(targetLanguages).find(
        (lang) => normalizeLanguageCode(lang.code) === normalizedCode
      );
      if (existingLanguage) {
        throw new Error(`Jazyk s kódem "${normalizedCode}" již existuje.`);
      }

      if (get(isOnline)) {
        try {
          const { data, error } = await supabase
            .from('flashcard_target_languages')
            .insert({
              name,
              code: normalizedCode,
              user_id: userId,
              is_custom: true,
              is_rtl: isRtl,
            })
            .select()
            .single();

          if (error) throw error;

          const customLanguage: FlashcardTargetLanguage = {
            id: data.id,
            name: data.name,
            code: data.code,
            is_custom: true,
            is_rtl: data.is_rtl ?? false,
          };

          // Add to store
          targetLanguages.update((langs) => [...langs, customLanguage]);

          // Cache it
          const updatedLanguages = get(targetLanguages);
          await offlineStorage.saveLanguages(updatedLanguages);

          // Reload languages from database to ensure sync
          await flashcardActions.loadData();

          return customLanguage;
        } catch (syncError) {
          console.error('Failed to add custom language:', syncError);
          throw syncError instanceof Error ? syncError : new Error('Nepodařilo se přidat jazyk');
        }
      } else {
        throw new Error('Pro přidání vlastního jazyka je nutné připojení k internetu.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nepodařilo se přidat vlastní jazyk';
      appError.set(message);
      throw err instanceof Error ? err : new Error(message);
    }
  },

  // Delete custom language
  deleteCustomLanguage: async (languageId: string): Promise<void> => {
    try {
      const userId = await getAuthenticatedUserId();
      if (!userId) {
        throw new Error('Uživatel není přihlášen.');
      }

      // Find the language to verify it's custom
      const language = get(targetLanguages).find((lang) => lang.id === languageId);
      if (!language) {
        throw new Error('Jazyk nebyl nalezen.');
      }

      // Only allow deletion if online
      if (!get(isOnline)) {
        throw new Error('Pro odstranění jazyka je nutné připojení k internetu.');
      }

      try {
        // Delete from server
        const { error } = await supabase
          .from('flashcard_target_languages')
          .delete()
          .eq('id', languageId)
          .eq('user_id', userId);

        if (error) throw error;

        // Remove from store - also remove related flashcards
        targetLanguages.update((langs) => langs.filter((lang) => lang.id !== languageId));
        flashcards.update((cards) => cards.filter((card) => card.targetLanguage.id !== languageId));
        categories.update((cats) => cats.filter((cat) => cat.targetLanguage.id !== languageId));

        // Update cache
        const updatedLanguages = get(targetLanguages);
        const updatedFlashcards = get(flashcards);
        const updatedCategories = get(categories);

        await Promise.all([
          offlineStorage.saveLanguages(updatedLanguages),
          offlineStorage.saveFlashcards(updatedFlashcards),
          offlineStorage.saveCategories(updatedCategories),
        ]);

        // If deleted language was selected, select another one
        if (get(selectedTargetLanguage)?.id === languageId) {
          const remainingLanguages = get(targetLanguages);
          selectedTargetLanguage.set(remainingLanguages[0] || null);
        }
      } catch (syncError) {
        console.error('Failed to delete custom language:', syncError);
        throw syncError instanceof Error ? syncError : new Error('Nepodařilo se smazat jazyk');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nepodařilo se smazat vlastní jazyk';
      appError.set(message);
      throw err instanceof Error ? err : new Error(message);
    }
  },
};
